import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote } from "lucide-react";

export default function Testimonials({ data }) {
  const reviews = (data?.items || []).filter((r) => r.enabled !== false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (reviews.length < 2) return;
    const id = setInterval(() => setI((p) => (p + 1) % reviews.length), 5000);
    return () => clearInterval(id);
  }, [reviews.length]);

  if (reviews.length === 0) return null;
  const safeIndex = Math.min(i, reviews.length - 1);
  const r = reviews[safeIndex];

  return (
    <section data-testid="testimonials-section" className="relative py-24 sm:py-32 px-6 bg-white">
      <div className="max-w-4xl mx-auto text-center">
        {data?.eyebrow && (
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#B8941F] font-semibold mb-4">{data.eyebrow}</p>
        )}
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A]">{data?.title}</h2>

        <div className="mt-14 relative min-h-[280px] sm:min-h-[240px]">
          <Quote className="absolute -top-6 left-1/2 -translate-x-1/2 w-14 h-14 text-[#D4AF37]/25" />
          <AnimatePresence mode="wait">
            <motion.div
              key={safeIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-[#EFEAD8] rounded-3xl p-10 sm:p-12 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.12)]"
              data-testid={`testimonial-${safeIndex}`}
            >
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <p className="font-display text-xl sm:text-2xl text-gray-800 leading-relaxed italic font-medium">
                &ldquo;{r.text}&rdquo;
              </p>
              <div className="mt-7">
                <div className="font-display text-base font-bold text-[#0A0A0A]">{r.name}</div>
                <div className="text-sm text-[#B8941F] tracking-wider">{r.role}</div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex justify-center gap-2" data-testid="testimonial-dots">
          {reviews.map((_, k) => (
            <button
              key={k}
              onClick={() => setI(k)}
              aria-label={`Show review ${k + 1}`}
              data-testid={`testimonial-dot-${k}`}
              className={`h-1.5 rounded-full transition-all ${
                safeIndex === k ? "w-8 bg-[#D4AF37]" : "w-3 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
